/**
 * StreamTheater - Reusable Theater Mode Component
 *
 * Full-screen stream viewer with embedded player, chat, and optional challenge panel.
 * Implements the "watch-to-play" loop from BUILDING_BLOCKS.md:
 * - Watch stream in theater mode
 * - See creator's active challenges
 * - Join queue directly from the theater
 */

import React, { useEffect, useCallback } from "react";
import { FaEye, FaExternalLinkAlt, FaTimes, FaComments, FaVolumeMute, FaVolumeUp, FaTrophy, FaGamepad, FaFire, FaChevronDown } from "react-icons/fa";
import { StreamingPlatform } from "@devour/client";
import { StreamWatchTimer } from "@/widgets/StreamWatchTimer";
import { lockScroll, unlockScroll } from "@/utils/scrollLock";
import { getChatEmbedUrl } from "./shared";
import "./StreamTheater.scss";
import {
    platformClass, getPlatformIcon, getPlatformUrl, getEmbedUrl, formatViewerCount,
} from "./stream-theater/theaterEmbeds";

export interface StreamData {
    _id: string;
    label: string;
    /*
     * OPTIONAL, because `FeaturedStreamResponse.platform` is.
     *
     * Platform is a property of a CHANNEL and a roster member is not required
     * to have one — a Finals Qualifier Contestant is on the roster by winning a
     * qualifier, not by streaming. This was required, and callers reached it by
     * casting (`as StreamData`), which silenced the mismatch and let `undefined`
     * through to the `.toLowerCase()` calls below. That is the same stale
     * required-`platform` local type that `web/src/hooks/hub/useGetHub.ts`
     * documents having caused this exact failure once already.
     *
     * `StreamingPlatform`, not a hand-written three-value union: the generated
     * type has FOUR members and `MULTI` is a real one. Writing the union out
     * here made every `matchingStream.platform` assignment a type error and
     * invited another cast to silence it. Everything below either switches with
     * a default or goes through the optional-safe helpers, so MULTI lands on
     * the neutral icon rather than on nothing.
     */
    platform?: StreamingPlatform;
    twitchChannelId?: string;
    kickChannelSlug?: string;
    youtubeChannelId?: string;
    youtubeLiveId?: string;
    isLive: boolean;
    viewerCount: number;
    profileImageUrl?: string;
    /**
     * Context line under the name — what this stream is PART of.
     *
     * The Live Hub sets the competition name here. Its own theater copy showed
     * that line and this component had no equivalent, so adopting the shared
     * one would have quietly dropped it: on a page that aggregates streams from
     * every competition at once, "which competition is this" is the one piece
     * of context a viewer cannot infer from the player.
     *
     * Optional, and omitted by the creator-hub callers, where the surrounding
     * page already answers it.
     */
    subtitle?: string;
    /*
     * Optional VOD context. When set, the theater plays this specific past
     * broadcast instead of the channel's live player. Twitch + YouTube have
     * public VOD iframes; Kick has no public VOD embed so it falls through to
     * its live-channel iframe (Kick's player renders its own offline screen
     * for past broadcasts).
     */
    vod?: {
        title?: string;
        /** Numeric Twitch video id (from twitch.tv/videos/<id>) */
        twitchVideoId?: string;
        /** YouTube video id (the v= param) */
        youtubeVideoId?: string;
    };
}

/**
 * Challenge data for the challenge panel
 * Matches the structure from HubChallengesSection
 */
export interface ChallengeData {
    id: string;
    creatorName: string;
    challengeTitle: string;
    challengeType: string;
    game: string;
    entryFee: number;
    entryFeeType: string;
    prizeAmount: number;
    prizeType: string;
    queueSize: number;
    queueFilled: number;
    currentStreak: number;
}

interface StreamTheaterProps {
    stream: StreamData | null;
    isOpen: boolean;
    onClose: () => void;
    showViewerCounts?: boolean;
    availableStreams?: StreamData[];
    onSwitchStream?: (stream: StreamData) => void;
    /** Active challenges for this creator */
    challenges?: ChallengeData[];
    /** Callback when user clicks "Join Queue" */
    onJoinQueue?: (challenge: ChallengeData) => void;
    /** Elapsed watch-time seconds from useStreamViewTracking */
    elapsedSeconds?: number;
}

/*
 * The platform helpers — icon, CSS modifier, embed / external URLs, and the
 * viewer-count format — live in `stream-theater/theaterEmbeds`. They are pure
 * functions of a `StreamData` and this file was past the 400-line ceiling. The
 * chat URL comes from the one shared builder in `shared.tsx`.
 */


export const StreamTheater = ({
    stream,
    isOpen,
    onClose,
    showViewerCounts = true,
    availableStreams = [],
    onSwitchStream,
    challenges = [],
    onJoinQueue,
    elapsedSeconds = 0,
}: StreamTheaterProps) => {
    const [showChat, setShowChat] = React.useState(true);
    const [isMuted, setIsMuted] = React.useState(false);
    const [showChallenges, setShowChallenges] = React.useState(true);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === "Escape" && isOpen) {
            onClose();
        }
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("keydown", handleKeyDown);
            lockScroll();
        }
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            if (isOpen) {
                unlockScroll();
            }
        };
    }, [isOpen, handleKeyDown]);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen || !stream) {
        return null;
    }

    const chatUrl = getChatEmbedUrl(stream);
    const liveStreams = availableStreams.filter(s => s.isLive);

    return (
        <div className="stream-theater" onClick={handleOverlayClick}>
            <div className="stream-theater__content">
                {/* Header */}
                <div className="stream-theater__header">
                    <div className="stream-theater__info">
                        <div className={`stream-theater__platform stream-theater__platform--${platformClass(stream.platform)}`}>
                            {getPlatformIcon(stream.platform)}
                        </div>
                        <span className="stream-theater__meta">
                            <span className="stream-theater__name">{stream.label}</span>
                            {stream.subtitle && <span className="stream-theater__subtitle">{stream.subtitle}</span>}
                        </span>
                        {showViewerCounts && stream.viewerCount > 0 &&
                            <span className="stream-theater__viewers">
                                <FaEye /> {formatViewerCount(stream.viewerCount)} watching
                            </span>
                        }
                        <StreamWatchTimer elapsedSeconds={elapsedSeconds} />
                    </div>
                    <div className="stream-theater__controls">
                        <button
                            className={`stream-theater__btn ${isMuted ? "stream-theater__btn--active" : ""}`}
                            onClick={() => setIsMuted(!isMuted)}
                            title={isMuted ? "Unmute" : "Mute"}
                        >
                            {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                        </button>
                        {challenges.length > 0 &&
                            <button
                                className={`stream-theater__btn ${showChallenges ? "stream-theater__btn--active" : ""}`}
                                onClick={() => setShowChallenges(!showChallenges)}
                                title={showChallenges ? "Hide Challenges" : "Show Challenges"}
                            >
                                <FaTrophy />
                            </button>
                        }
                        {chatUrl &&
                            <button
                                className={`stream-theater__btn ${showChat ? "stream-theater__btn--active" : ""}`}
                                onClick={() => setShowChat(!showChat)}
                                title={showChat ? "Hide Chat" : "Show Chat"}
                            >
                                <FaComments />
                            </button>
                        }
                        <a
                            href={getPlatformUrl(stream)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="stream-theater__btn"
                            /*
                             * A TEMPLATE LITERAL, so an absent platform would
                             * stringify to the words "Open on undefined" on the
                             * tooltip — the one `.platform` read in this file
                             * that a JSX-child guard would not have caught.
                             */
                            title={stream.platform ? `Open on ${stream.platform}` : "Open externally"}
                        >
                            <FaExternalLinkAlt />
                        </a>
                        <button
                            className="stream-theater__btn stream-theater__btn--close"
                            onClick={onClose}
                            title="Close (ESC)"
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* Main Content - Player + Challenges + Chat */}
                <div className={`stream-theater__main ${showChat && chatUrl ? "" : "stream-theater__main--no-chat"} ${showChallenges && challenges.length > 0 ? "stream-theater__main--with-challenges" : ""}`}>
                    <div className="stream-theater__player">
                        <iframe
                            src={getEmbedUrl(stream, isMuted)}
                            frameBorder="0"
                            allowFullScreen
                            allow="autoplay; fullscreen"
                            title={`${stream.label} stream`}
                        />

                        {/* Challenge Panel - Overlay on player */}
                        {showChallenges && challenges.length > 0 &&
                            <div className="stream-theater__challenges">
                                <div className="stream-theater__challenges-header">
                                    <FaGamepad size={16} />
                                    <span>{stream.label.toUpperCase()}'S LIVE CHALLENGES</span>
                                    <button
                                        className="stream-theater__challenges-toggle"
                                        onClick={() => setShowChallenges(false)}
                                    >
                                        <FaChevronDown />
                                    </button>
                                </div>
                                <div className="stream-theater__challenges-list">
                                    {challenges.map((challenge) =>
                                        <div key={challenge.id} className="stream-theater__challenge-card">
                                            <div className="stream-theater__challenge-title">
                                                <FaTrophy size={14} />
                                                {challenge.challengeTitle}
                                            </div>
                                            <div className="stream-theater__challenge-meta">
                                                <span>
                                                    Entry: {challenge.entryFee === 0 ? "FREE" : `${challenge.entryFee} ${challenge.entryFeeType}`}
                                                </span>
                                                <span className="stream-theater__challenge-prize">
                                                    Prize: {challenge.prizeAmount} {challenge.prizeType}
                                                </span>
                                            </div>
                                            <div className="stream-theater__challenge-queue">
                                                <div className="stream-theater__challenge-queue-bar">
                                                    <div
                                                        className="stream-theater__challenge-queue-fill"
                                                        style={{ width: `${challenge.queueFilled / challenge.queueSize * 100}%` }}
                                                    />
                                                </div>
                                                <span>{challenge.queueFilled}/{challenge.queueSize} in queue</span>
                                                {challenge.currentStreak > 0 &&
                                                    <span className="stream-theater__challenge-streak">
                                                        <FaFire size={10} /> {challenge.currentStreak} win streak
                                                    </span>
                                                }
                                            </div>
                                            {onJoinQueue &&
                                                <button
                                                    className="stream-theater__challenge-join"
                                                    onClick={() => onJoinQueue(challenge)}
                                                >
                                                    JOIN QUEUE
                                                </button>
                                            }
                                        </div>)}
                                </div>
                            </div>
                        }
                    </div>
                    {showChat && chatUrl &&
                        <div className="stream-theater__chat">
                            <iframe
                                src={chatUrl}
                                frameBorder="0"
                                title={`${stream.label} chat`}
                            />
                        </div>
                    }
                </div>

                {/* Stream Selector Bar */}
                {liveStreams.length > 1 && onSwitchStream &&
                    <div className="stream-theater__selector">
                        <span className="stream-theater__selector-label">
                            Switch Stream:
                        </span>
                        <div className="stream-theater__selector-streams">
                            {liveStreams.map((s) =>
                                <button
                                    key={s._id}
                                    onClick={() => onSwitchStream(s)}
                                    className={`stream-theater__selector-item ${stream._id === s._id ? "stream-theater__selector-item--active" : ""}`}
                                >
                                    <div className={`stream-theater__selector-icon stream-theater__selector-icon--${platformClass(s.platform)}`}>
                                        {getPlatformIcon(s.platform)}
                                    </div>
                                    <span>{s.label}</span>
                                    {showViewerCounts && s.viewerCount > 0 &&
                                        <span className="stream-theater__selector-viewers">
                                            {formatViewerCount(s.viewerCount)}
                                        </span>
                                    }
                                </button>)}
                        </div>
                    </div>
                }
            </div>
        </div>
    );
};
